/**
 * Authentication service for BOSI frontend.
 *
 * Provides functions for login, logout, register, and token refresh
 * using localStorage-based token management.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

interface LoginResponse {
  user: any;
  access_token: string;
  refresh_token: string;
}

interface RegisterResponse {
  user: any;
  access_token: string;
  refresh_token: string;
}

/**
 * Store tokens in localStorage.
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Get access token from localStorage.
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get refresh token from localStorage.
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Clear all tokens from localStorage.
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Login user with email and password.
 * Stores tokens in localStorage.
 */
export async function login(email: string, password: string): Promise<LoginResponse> {
  // Clear old tokens before login
  clearTokens();

  const response = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const error: any = new Error(errorData.detail || 'Login failed');
    error.response = { data: errorData };
    throw error;
  }

  const data = await response.json();

  // Store tokens in localStorage
  if (data.access_token && data.refresh_token) {
    setTokens(data.access_token, data.refresh_token);
  }

  return data;
}

/**
 * Register a new user account.
 * Stores tokens in localStorage.
 */
export async function register(
  email: string,
  password: string,
  profile?: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    telephone?: string;
    age_range: string;
    life_stage: string;
    primary_goal: string;
  }
): Promise<RegisterResponse> {
  // Clear old tokens before registration
  clearTokens();

  // Feature 016: profile is optional - simplified registration
  const body = profile
    ? { email, password, profile }
    : { email, password };

  const response = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json();
    const error: any = new Error(errorData.detail || 'Registration failed');
    error.response = { data: errorData };
    throw error;
  }

  const data = await response.json();

  // Store tokens in localStorage
  if (data.access_token && data.refresh_token) {
    setTokens(data.access_token, data.refresh_token);
  }

  return data;
}

/**
 * Logout current user.
 * Clears tokens from localStorage.
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    // Ignore errors, clear tokens anyway
  } finally {
    clearTokens();
  }
}

/**
 * Refresh access token using refresh token.
 * Updates access token in localStorage.
 */
export async function refreshToken(): Promise<string> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    clearTokens(); // Clear invalid tokens
    throw new Error('Token refresh failed');
  }

  const data = await response.json();

  // Update access token in localStorage
  if (data.access_token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  }

  return data.access_token;
}

/**
 * Get current authenticated user.
 * Requires Authorization header with access token.
 */
export async function getCurrentUser(): Promise<any> {
  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  try {
    const response = await fetch(`${API_URL}/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Not authenticated');
    }

    return response.json();
  } catch (error) {
    throw new Error('Not authenticated');
  }
}
